import {makeObservable, flow, observable, computed, action, flowResult} from 'mobx'
import type {CommerceAPI} from 'commerce-api'
import {
  ContentClient,
  StagingEnvironmentFactory,
  ContentItem,
  DefaultContentBody,
  ContentBody,
  HierarchyContentItem,
  ByIdContentClientHierarchyRequest,
} from 'dc-delivery-sdk-js'
import type {ContentClientConfigOptions} from 'dc-delivery-sdk-js/build/main/lib/config'
import type {FetchResponse} from 'dc-delivery-sdk-js/build/main/lib/content/model/Fetch'
import type {RootStore} from './RootStore'
import type {StoreModel} from './utils'
import {
  ContentItemResponse,
  FilterByResponse,
} from 'dc-delivery-sdk-js/build/main/lib/content/model/FilterBy'
import {
  PDPAwardSlot,
  StickyBanner,
  BASPageContent,
  OrderHistoryHeroBanners,
} from '../types/cms'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {Nullable} from '../types/utils'
import {useContentToDisplay} from '../components/cms/Slot'
import {SlotContentItem} from '../types/cms'
import { CmsRoute, NavContentItem, NavigationMenu, NavItem } from '../types/cms/navigation'
import { ContentParams, ContentStoreInitialState, HierarchyNode } from '../types/store/content'
import { Status, StatusMap } from '../types/commerce'
import { DELIVERY_KEYS } from '../constants'
import { Hit, ProductModel } from './ProductStore'
import { DateUtils } from '../utils/date-utils'

export class ContentStore implements StoreModel {
  rootStore: RootStore
  api: CommerceAPI
  client: ContentClient
  contentById: Record<string, DefaultContentBody>
  lastPublishedDates: Record<string, string>
  lastPublishedDateRequests: Partial<Record<string, Promise<string | null>>>
  contentClientConfig: ContentClientConfigOptions
  params: ContentParams | undefined
  navMenu: NavigationMenu
  routeMap: Record<string, string>
  filterFetchPending?: boolean | undefined | Promise<any>
  selectedPreviewCustomerGroup: Nullable<string>
  pdpAwardSlots: PDPAwardSlot[]
  stickyBanners: StickyBanner[]
  status: Status
  BASHeroes: BASPageContent[]
  BASFooters: BASPageContent[]
  orderHistoryHeroBanners: OrderHistoryHeroBanners[]

  constructor(rootStore: RootStore, initialState: ContentStoreInitialState) {
    this.api = rootStore.api
    this.rootStore = rootStore
    this.contentById = initialState.contentById || {}
    this.lastPublishedDates = initialState.lastPublishedDates || {}
    this.lastPublishedDateRequests = {}
    this.routeMap = initialState.routeMap || {}
    this.navMenu = initialState.navMenu || []
    this.contentClientConfig = initialState.contentClientConfig
    this.params = initialState.params || {}
    this.selectedPreviewCustomerGroup = initialState?.params?.previewCustomerGroup ?? null
    this.pdpAwardSlots = []
    this.stickyBanners = initialState?.stickyBanners || []
    this.status = StatusMap.INIT
    this.BASHeroes = initialState?.BASHeroes || []
    this.BASFooters = initialState?.BASFooters || []
    this.orderHistoryHeroBanners = initialState?.orderHistoryHeroBanners || []
    this.status = StatusMap.INIT

    makeObservable(this, {
      contentById: observable,
      lastPublishedDates: observable,
      pdpAwardSlots: observable,
      stickyBanners: observable,
      orderHistoryHeroBanners: observable,
      navMenu: observable,
      routeMap: observable,
      selectedPreviewCustomerGroup: observable,
      status: observable,
      BASHeroes: observable,
      BASFooters: observable,
      isVisualisation: computed,
      fetchItemByKey: flow.bound,
      fetchBadgesForHits: flow.bound,
      fetchItemById: flow.bound,
      fetchItems: flow.bound,
      setPreviewTimestamp: flow.bound,
      buildFooterLinks: flow.bound,
      fetchItemLastPublishedDate: action.bound,
      setLastPublishedDates: action.bound,
      fetchItemsByContentType: flow.bound,
      buildNavigationMenu: flow.bound,
      buildRoutingMap: flow.bound,
      buildRoutingMapAndNavMenu: flow.bound,
      setSelectedPreviewCustomerGroup: action.bound,
      setPdpAwardSlots: action.bound,
      setOrderHistoryHeroBanners: action.bound,
      setNavMenu: action.bound,
      setRouteMap: action.bound,
      setBASHeroes: action.bound,
      setBASFooters: action.bound,
      setFilterFetchPending: action.bound,
      setStatus: action.bound,
    })

    /**
     * Handle visualization flow by setting the SDK domain to the provided VSE domain
     */
    if (this.params?.vseDomain) {
      this.contentClientConfig.stagingEnvironment = this.params.vseDomain
    }

    /**
     * Create and store an instance of the Amplience client
     */
    this.client = new ContentClient(this.contentClientConfig)

    this.setFilterFetchPending(undefined)

    this.rootStore = rootStore
  }

  get isVisualisation() {
    const path =
      typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`

    return path?.includes('/visualization?') || /\/visualization$/.test(path)
  }

  get loading() {
    return this.status === StatusMap.PENDING
  }

  setStatus(value: Status) {
    this.status = value
  }

  getValidItems(items: StickyBanner[] | BASPageContent[] | OrderHistoryHeroBanners[]) {
    if (!items) {
      return null
    }
    const validItems: typeof items = []
    items
      .map((item) => {
        const bannersToDisplay = useContentToDisplay(item as unknown as SlotContentItem)
        if (bannersToDisplay) {
          /* @TODO Content Types need rebuilt to better handle differences. Here the StickBanner and BASPAgeContent types
          are now incompatible with the generic SlotContentItem that everything is expecting
          @ts-ignore */
          validItems.push(item)
        }
        return bannersToDisplay
      })
      .filter(Boolean)
    return validItems
  }

  get validBASHeroes() {
    return this.getValidItems(this.BASHeroes)
  }

  get validStickyBanners() {
    return this.getValidItems(this.stickyBanners)
  }

  get validBASFooters() {
    return this.getValidItems(this.BASFooters)
  }

  get validOrderHistoryHeroBanners() {
    return this.getValidItems(this.orderHistoryHeroBanners)
  }

  setPdpAwardSlots(value: PDPAwardSlot[]) {
    this.pdpAwardSlots = value
  }

  setStickyBanners(value: StickyBanner[]) {
    this.stickyBanners = value
  }

  setOrderHistoryHeroBanners(value: OrderHistoryHeroBanners[]) {
    this.orderHistoryHeroBanners = value
  }

  setNavMenu(value: NavItem[]) {
    this.navMenu = value
  }

  setRouteMap(value: Record<string, string>) {
    this.routeMap = value
  }

  setSelectedPreviewCustomerGroup(customerGroupId: Nullable<string>) {
    this.selectedPreviewCustomerGroup = customerGroupId
  }

  setLastPublishedDates(value: Record<string, string>) {
    this.lastPublishedDates = value
  }

  async fetchItemLastPublishedDate(deliveryId: string, forceFetch = false): Promise<string | null> {
    if (deliveryId in this.lastPublishedDates && !forceFetch) {
      return this.lastPublishedDates[deliveryId]
    }

    const pendingRequest = this.lastPublishedDateRequests[deliveryId]
    if (pendingRequest && !forceFetch) {
      return pendingRequest
    }

    const request = (async () => {
      try {
        const response = await fetch(`/api/last-published-date?contentId=${deliveryId}`)
        const result = await response.json().catch(() => ({lastPublishedDate: null}))
        const lastPublishedDate = result?.lastPublishedDate ?? null

        if (lastPublishedDate) {
          this.setLastPublishedDates({
            ...this.lastPublishedDates,
            [deliveryId]: DateUtils.format(lastPublishedDate, 'dd/MM/yyyy'),
          })
        }

        return lastPublishedDate
      } catch (error) {
        console.error('Failed to fetch last published date', error)
        return null
      } finally {
        delete this.lastPublishedDateRequests[deliveryId]
      }
    })()

    this.lastPublishedDateRequests[deliveryId] = request
    return request
  }

  setBASHeroes(value: BASPageContent[]) {
    this.BASHeroes = value
  }

  setBASFooters(value: BASPageContent[]) {
    this.BASFooters = value
  }

  setFilterFetchPending(value: boolean | undefined | Promise<any>) {
    this.filterFetchPending = value
  }

    /**
   * The preview URL contains a `vseDomain` that pinned to the time it was created.
   * We can allow for changing this time dynamically by recreating the `vseDomain`
   * with a new timestamp. The SDK provides a helper method for this, so we use
   * that, update our config, and recreate the sdk client.
   *
   * @note - This is intended to be used with the x-ray/preview panel when implemented.
   */
    *setPreviewTimestamp(timestamp: number) {
      if (this.params?.isContentPreview && this.params.vseDomain) {
        const factory = new StagingEnvironmentFactory(this.params.vseDomain)
        const updatedDomain: string = yield factory.generateDomain({
          timestamp,
        })
        this.contentClientConfig.stagingEnvironment = updatedDomain
        this.client = new ContentClient(this.contentClientConfig)
  
        return updatedDomain
      }
    }

  /**
   * @description Fetches a content item by its delivery key
   * @param {string} deliveryKey The delivery key of the content item to fetch
   * @param {boolean} forceFetch Flag to force fetch regardless of whether we already have the item
   * @returns 
   */
  *fetchItemByKey(deliveryKey: string, forceFetch = false) {
    /** Skip fetching if we already have the content. */
    if (deliveryKey in this.contentById && !forceFetch) {
      return
    }

    try {
      const result: ContentItem = yield this.client.getContentItemByKey(deliveryKey)
      /**
       * Note that we are calling `toJSON` on the sdk result. This ensures we are only
       * storing plain objects instead of the enhanced objects returned by the sdk.
       */
      this.contentById[deliveryKey] = result.toJSON()
    } catch (error) {
      console.error('Failed to fetch', error)
    }
  }



*fetchBadgesForHits(hits: Hit[] | ProductModel[]){
    const hitsWithBadges = []
    for (const hit of hits) {
        if (!hit) {
            continue
        }
        const badgeAssetId = hit.pdpBadgeAsset || ''
        if (badgeAssetId && !this.contentById[badgeAssetId]) {
            yield this.fetchItemByKey(badgeAssetId)
        }
        hitsWithBadges.push({ id: hit.id, badge: this.contentById[badgeAssetId] ?? null })
    }
    return hitsWithBadges
}


  /**
   * @description Fetches all content items referenced in a Hierarchy Node
   * @param {HierarchyNode} hierarchy the Hierarchy Node containing a complete Hierarchy
   */
  *fetchContentForHierarchy(hierarchy: HierarchyNode)  {
    // Fetch all the IDs of the linked content items in the hierarchy so we can use them at render
    const idList = []

    // loop over hierarchy and children to get the IDs
    for(const node of hierarchy.children) {
      // Only process children of the top level nodes
      if(node.children) {
        for(const childNode of node.children) {
          // if content is a route, get the ID and add it to our list to fetch
          if(childNode.content.route) {
            idList.push({'id': childNode.content.route.id})
          }
        }
      }
    }

    // Fetch the content for the IDs in a single call
    yield this.fetchItems(idList, {depth: 'root', format: 'inlined'});
  }

  /**
   * @description Fetches a Hierarchy from Amplience starting from a given rootId
   * NB: the Hierarchy DOES NOT RETURN ANY CONTENT, THAT WILL NEED FETCHING INDEPENDENTLY
   * @param {string} rootId the delivery ID of the root content item to build the Hierarchy from
   * @param {number} maximumDepth THe maximum depth of the Hierarchy to fetch
   * @param {number} maximumPageSize The maximum number of items to fetch per page
   * @param {ContentItem} rootItem An optional root item to use instead of fetching it
   * @param {string} sortkey the key to sort the hierarchy by (Defaults to Ranking)
   * @param {'ASC' | 'DESC'} sortOrder the order to sort the hierarchy (Defaults to ASC)
   * @returns {DefaultContentBody} The fetched Hierarchy Content Item
   */
  *fetchHierarchy(
    rootId: string,
    maximumDepth?: number,
    maximumPageSize?: number,
    rootItem?: ContentItem,
    sortkey?: string,
    sortOrder?: 'ASC' | 'ASC'
  ): Generator<Promise<HierarchyContentItem<DefaultContentBody>>, DefaultContentBody, DefaultContentBody> {      
    // Build a request for the Hierarchy based on function inputs and defaults
    const requestParams: ByIdContentClientHierarchyRequest = {
      rootId: rootId,
      maximumDepth: maximumDepth || 2,
      maximumPageSize: maximumPageSize || 50,
      rootItem: rootItem || undefined,
      sortKey: sortkey || 'default',
      sortOrder: sortOrder || 'ASC'
    }
    
    // Fetch the Hierarchy from the SDK
    const hierarchy = yield this.client.getByHierarchy(requestParams)
    return hierarchy
  }

  /** Builds the Footer Links Object */
  *buildFooterLinks() {
    // Object to store the fetched hierarchy
    let hierarchy: DefaultContentBody = {} as DefaultContentBody

    try {
      // Get the footer-links content item to find the root node ID
      const result: ContentItem = yield this.client.getContentItemByKey(DELIVERY_KEYS.FOOTER)

      if (result) {
        // Extract the root node iD
        const response = result.toJSON()
        const {
          _meta: {deliveryId: rootNodeId},
        } = response

        // Fetch the Hierarchy starting from the root node ID
        hierarchy = yield* this.fetchHierarchy(rootNodeId)
        //Fetch the content for those items on the heirarchy
        yield* this.fetchContentForHierarchy(hierarchy)

        // Set the content of footer-links in the store
        this.contentById[DELIVERY_KEYS.FOOTER] = hierarchy
      }
    } catch (error) {
      console.error('Failed to fetch hierarchy', error)
    }
  }

  /**
   * @description Fetches a content item by its delivery ID
   * @param {string} deliveryId Delivery ID of the content item to fetch
   * @param {boolean} forceFetch Whether to force fetch the content item even if it already exists in the store
   */
  *fetchItemById(deliveryId: string, forceFetch = false) {
    /** Skip fetching if we already have the content. */
    if (deliveryId in this.contentById && !forceFetch) {
      return
    }

    try {
      const result: ContentItem = yield this.client.getContentItemById(deliveryId)

      /**
       * Note that we are calling `toJSON` on the sdk result. This ensures we are only
       * storing plain objects instead of the enhanced objects returned by the sdk.
       */
      this.contentById[deliveryId] = result.toJSON()
    } catch (error) {
      console.error('Failed to fetch', error)
    }
  }

  /**
   * Fetch multiple items, by key or id
   * https://github.com/amplience/dc-delivery-sdk-js#fetch-content-items
   */
  *fetchItems(
    requests: Array<{id: string} | {key: string}>,
    parameters: Parameters<typeof this.client.fetchContentItems>[0]['parameters'] = {
      format: 'inlined',
      depth: 'all',
    },
  ) {
    /** Filter out requests that have already been made */
    const filteredRequests = requests.filter((req) => {
      if (
        ('key' in req && this.contentById[req.key]) ||
        ('id' in req && this.contentById[req.id])
      ) {
        return false
      }
      return true
    })

    /** Skip fetching if no requests remain */
    if (filteredRequests.length < 1) {
      return
    }

    const failedRequests: Array<string> = []

    // Set the Limit on number of items in each request to match Amplience limit
    const maxChunkSize = 12

    // Break the requests into smaller chunks due to limitations on items per request
    for (let i = 0; i < filteredRequests.length; i += maxChunkSize) {
      const chunk = filteredRequests
        // Create chunk of maxChunkSize
        .slice(i, i + maxChunkSize)
        // Remove identical objects
        .filter((item, index, self) => 
          index === self.findIndex((arrayItem) => JSON.stringify(arrayItem) === JSON.stringify(item))
        )
      try {
        // Fetch the items in this Chunk
        const result: FetchResponse<any> = yield this.client.fetchContentItems({
          requests: chunk,
          parameters,
        })

        // Store the results and failures
        result.responses.forEach((res, idx) => {
          if ('error' in res && res.error) {
            // @TODO resolve issue with this - Amplience SDK does not contain type for `data` yet it exists and is needed here
            // @ts-ignore
            const deliveryKey = res.error.data.deliveryKey
            console.warn(`Failed to fetch [${deliveryKey}] asset from Amplience.`)
            failedRequests.push(deliveryKey)
          }

          if ('content' in res) {
            const req = chunk[idx]
            let storageKeyOrId = ''
            if ('key' in req) storageKeyOrId = req.key
            if ('id' in req) storageKeyOrId = req.id
            if (storageKeyOrId) {
              this.contentById[storageKeyOrId] = res.content
            }
          }
        })
      } catch (error) {
        console.error('Failed to fetch', error)
        // To prevent re-calls for items that were in this failed request, Add them to the failedRequests list here
        chunk.forEach((item) => {
          const itemKey = 'key' in item ? item.key : item.id
          console.warn(`Failed to fetch [${itemKey}] asset from Amplience.`)
          failedRequests.push(itemKey)
        })
      }
    }

    return failedRequests
  }

  *buildNavigationMenu() {
    const root: FilterByResponse<ContentBody> = yield this.client
      .filterByParentId(this.params!.navigationContentId!)
      .request()

    const navItemPromises = root.responses.map(({content}) =>
      this.client.getContentItemById(content._meta.deliveryId).then((r) => {
        return r.toJSON()
      }),
    )

    const results: NavContentItem[] = yield Promise.all(navItemPromises)

    results.sort((curr, next) => {
      const currRanking = curr?.menuDefinition?.ranking ?? Number.MAX_SAFE_INTEGER
      const nextRanking = next?.menuDefinition?.ranking ?? Number.MAX_SAFE_INTEGER

      if (currRanking < nextRanking) {
        return -1
      } else if (currRanking > nextRanking) {
        return 1
      }
      return 0
    })

    function transformNavItem(item: NavContentItem) {
      const {menuDefinition, menuContent, route, flyOutContent} = item
      const result: NavItem = {
        name: menuDefinition?.navDisplayName,
        categoryId: item.route?.page?.categoryId,
        categoryName: item.route?.page?.name,
        children: item
      }

      const children = menuContent?.slice().sort((curr, next) => {
        const currRanking = curr?.menuDefinition?.ranking ?? Number.MAX_SAFE_INTEGER
        const nextRanking = next?.menuDefinition?.ranking ?? Number.MAX_SAFE_INTEGER

        if (currRanking < nextRanking) {
          return -1
        } else if (currRanking > nextRanking) {
          return 1
        }
        return 0
      })

      // Note that we want to keep the resulting NavItem objects as small as possible.
      // That means that we only add the properties that have a value. This way we don't
      // end up with `null` or `undefined` taking up space in the object.
      // We also can assume that default `color` is black and only keep the color field
      // if It's something different.

      if (route?.path) {
        result.path = route.path
      }

      if (menuContent && menuContent.length > 0 && Array.isArray(children) && children.length) {
        result.children = children?.map((mc) => transformNavItem(mc))
      }

      if (menuDefinition.mobileIcon) {
        result.icon = menuDefinition.mobileIcon
      }

      if (menuDefinition.menuTextColor && menuDefinition.menuTextColor.name !== 'Black') {
        result.color = menuDefinition.menuTextColor.color
      }

      if (flyOutContent && flyOutContent.default?.length) {
        result.flyOutContent = flyOutContent
      }

      return result
    }

    this.setNavMenu(results.map((item) => transformNavItem(item)))
  }

  *buildRoutingMap() {
    if (Object.keys(this.routeMap).length > 0 || typeof window !== 'undefined') {
      return
    }

    if (typeof global !== 'undefined' && global.__routeMap) {
      /** @todo - should this be conditional for local dev only? */
      this.setRouteMap(global.__routeMap)
      return
    }

    let mapping: Response | undefined
    if (this.filterFetchPending) {
      yield this.filterFetchPending
    } else {
      this.setFilterFetchPending(true)
      mapping = yield fetch(`${getAppOrigin()}/api/amplience/cached`)
      const mappingJson: Record<string, string> = yield mapping!.json()
      this.setRouteMap(mappingJson)
      this.setFilterFetchPending(undefined)
    }

    if (typeof global !== 'undefined') {
      /** @todo - should this be conditional for local dev only? */
      global.__routeMap = mapping
    }

    // If we already have the route map or we are in browser, don't build again
    if (Object.keys(this.routeMap).length > 0 || typeof window !== 'undefined') {
      return
    }

    // If fetching/building are in progress and we're called again, we wait.
    if (this.filterFetchPending) {
      yield this.filterFetchPending
    } else {
      // Return the locally cached data if available.
      if (typeof global !== 'undefined' && global.__routeMap) {
        /** @todo - should this be conditional for local dev only? */
        this.setRouteMap(global.__routeMap)
        this.setFilterFetchPending(undefined)
        return
      }

      this.setFilterFetchPending(build())
      return this.filterFetchPending
    }

    async function build() {
      const templateMapping: Record<string, string> = {
        'https://www.iceland.co.uk/page/category-page.json': 'CATEGORY',
      }

      let results: Array<ContentItemResponse<CmsRoute>> = []
      const mapping: Record<string, string> = {}
      const url = 'https://www.iceland.co.uk/route.json'
      const client: ContentClient = (self as any)!.client

      const result: FilterByResponse<CmsRoute> = await client
        .filterBy('/_meta/schema', url)
        .request({
          format: 'inlined',
          depth: 'all',
        })

      await paginate(result)

      async function paginate(result: FilterByResponse<CmsRoute>) {
        results = [...results, ...result.responses]

        if (result.page?.nextCursor && typeof result.page?.next === 'function') {
          const next: FilterByResponse<CmsRoute> = await result.page.next()
          await paginate(next)
        }
      }

      results.forEach(({content}) => {
        const template = templateMapping[content.page?._meta?.schema || ''] || 'CONTENT'
        mapping[
          `${content.path}`
        ] = `${content?.page?._meta.deliveryId}|${template}|${content.page?.categoryId}`
      })

      self.routeMap = mapping

      // Cache the data in a global server-side variable. This is meant for
      // local development only.
      if (typeof global !== 'undefined') {
        /** @todo - should this be conditional for local dev only? */
        global.__routeMap = mapping
      }

      self.filterFetchPending = undefined
    }
  }

  *buildRoutingMapAndNavMenu() {
    let routeMapJson
    let navMenuJson
    if (Object.keys(this.routeMap).length > 0 || typeof window !== 'undefined') {
      return
    }

    if (
      typeof global !== 'undefined' &&
      global.__routeMap &&
      global.__navMenu &&
      process.env.APP_ORIGIN === 'http://localhost:3000'
    ) {
      /** @todo - should this be conditional for local dev only? */
      this.setRouteMap(global.__routeMap)
      this.setNavMenu(global.__navMenu)
      return
    }
    async function build(self: any) {
      const routeMap = await fetch(`${getAppOrigin()}/amplience/routemap`)
      routeMapJson = await routeMap.json()
      const navMenu = await fetch(`${getAppOrigin()}/amplience/navmenu`)
      navMenuJson = await navMenu.json()
      self.routeMap = routeMapJson
      self.navMenu = navMenuJson
      self.filterFetchPending = undefined

      if (typeof global !== 'undefined' && process.env.APP_ORIGIN === 'http://localhost:3000') {
        /** @todo - should this be conditional for local dev only? */
        global.__routeMap = routeMapJson
        global.__navMenu = navMenuJson
      }
    }

    if (this.filterFetchPending) {
      yield this.filterFetchPending
    } else {
      this.setFilterFetchPending(build(this))
      return this.filterFetchPending
    }
  }

  *fetchItemsByContentType(url: string, stateProperty: string) {
    const client: ContentClient = this.client
    async function fetchData() {
      const result = await client.filterBy('/_meta/schema', url).request({
        format: 'inlined',
        depth: 'all',
      })
      return result
    }
    const filterByResponse: FilterByResponse<StickyBanner> = yield flowResult(fetchData())
    if (!filterByResponse || !filterByResponse.responses?.length) {
      return null
    }
    const contentItems = filterByResponse.responses.map(({content}) => content).filter(Boolean)
    switch (stateProperty) {
      case 'BASHeroes':
        this.setBASHeroes(contentItems)
        break
      case 'BASFooters':
        this.setBASFooters(contentItems)
        break
      case 'stickyBanners':
        this.setStickyBanners(contentItems)
        break
      case 'pdpAwardSlots':
        this.setPdpAwardSlots(contentItems)
        break
      case 'orderHistoryHeroBanners':
        this.orderHistoryHeroBanners = contentItems
        break
      default:
        throw new Error(`Unknown state property: ${stateProperty}`)
    }
  }

  get asJson() {
    return {
      contentById: this.contentById,
      contentClientConfig: this.contentClientConfig,
      navMenu: this.navMenu,
      params: this.params,
      routeMap: this.routeMap,
      selectedPreviewCustomerGroup: this.selectedPreviewCustomerGroup, 
      status: this.status,
    }
  }
}
